import type {
	CameraStorageKeyEntry,
	CameraStorageKeyPayload,
	CameraStorageKeyResponse,
	CloudMediaSettings,
	CloudRecording,
	CloudRecordingOverviewResponse,
	CloudRecordingParams,
	CloudRecordingResponse,
	DeviceThumbnailResponse,
	GStorageSignedUrlResponse,
} from 'src/components/Camera/types/cloud-recordings';
import { getCameraCloudSignedUrl } from './gcs-auth';
import { cameraCloudApi } from '../http';
import qs from 'qs';

const DELETE_RECORDINGS_TIMEOUT_MS = 60000;

/**
 * Get a signed URL granting read access to the user's cloud recordings.
 *
 * @returns Signed URL response with temporary access credentials
 */
export const getGStorageSignedUrl = async (): Promise<GStorageSignedUrlResponse> => {
	const result = (await cameraCloudApi.get('/auth/gstorage')) as GStorageSignedUrlResponse;
	return result;
};

/**
 * Append signed URL query params to a cloud media URL for authenticated access.
 *
 * @param baseUrl The base URL of the cloud media resource
 * @returns The URL with signed URL query param appended
 */
export const buildCloudMediaUrl = async (baseUrl: string): Promise<string> => {
	if (!baseUrl) return '';

	const token = await getCameraCloudSignedUrl();
	if (!token) return baseUrl;
	const separator = baseUrl.includes('?') ? '&' : '?';
	const result = `${baseUrl}${separator}${token}`;
	return result;
};

/**
 * Get cloud recordings for a specific device.
 *
 * @param deviceId The device ID
 * @param params Optional paging/filter params
 * @returns The device's cloud recordings
 */
export const getCloudRecordings = async (
	deviceId: string,
	params?: CloudRecordingParams,
): Promise<CloudRecordingResponse> => {
	if (!deviceId) return { items: [], limit: 0, offset: 0, total: 0 };
	const queryParams = params ? `?${qs.stringify(params)}` : '';
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}${queryParams}`,
	)) as CloudRecordingResponse;

	return result;
};

/**
 * Get a per-day overview of a device's cloud recordings, newest day first.
 *
 * @param deviceId The device ID
 * @returns Per-day video counts for the device
 */
export const getDeviceRecordingsOverview = async (
	deviceId: string,
): Promise<CloudRecordingOverviewResponse> => {
	if (!deviceId) return { days: [] };
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}/overview`,
	)) as CloudRecordingOverviewResponse;

	return result ?? { days: [] };
};

/**
 * Get a single cloud recording by its ID
 *
 * @param recordingId The recording ID
 * @returns The recording object or null if not found
 */
export const getRecordingById = async (recordingId: string): Promise<CloudRecording | null> => {
	if (!recordingId) return null;
	const result = (await cameraCloudApi.get(`/v1/recordings/${recordingId}`)) as CloudRecording;
	return result;
};

export const deleteCloudRecording = async (recordingId: string) => {
	if (!recordingId) return;
	await cameraCloudApi.delete(`/v1/recordings/${recordingId}/`);
};

/**
 * Bulk-delete a device's cloud recordings. With a date (YYYY-MM-DD) only that
 * day is removed, otherwise all recordings for the device are deleted.
 *
 * @param deviceId The device ID
 * @param date Optional calendar day (YYYY-MM-DD) to limit deletion to
 * @returns The number of deleted recordings
 */
export const deleteDeviceRecordings = async (
	deviceId: string,
	date?: string,
): Promise<{ deleted: number }> => {
	const query = date ? `?date=${date}` : '';
	const res = (await cameraCloudApi.delete(`/v1/recordings/device/${deviceId}${query}`, {
		timeout: DELETE_RECORDINGS_TIMEOUT_MS,
	})) as {
		data?: { deleted: number };
	};
	return res?.data ?? { deleted: 0 };
};

/**
 * Get the cloud settings for a device.
 *
 * @param deviceId The device ID
 * @returns The device's cloud settings
 */
export const getDeviceMediaSettings = async (deviceId: string): Promise<CloudMediaSettings | null> => {
	if (!deviceId) return null;
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}/settings`,
	)) as CloudMediaSettings;
	return result ?? null;
};

/**
 * Stores a device's encrypted recording-key backup in the cloud.
 *
 * @param payload - The device MAC plus the encrypted blob and its HMAC.
 * @returns The stored backup entry, including the backend-assigned `createdAt` era.
 */
export const setCameraStorageKey = async (
	payload: CameraStorageKeyPayload,
): Promise<CameraStorageKeyEntry> => {
	const { mac, blob, hmac } = payload;
	const res = (await cameraCloudApi.put('/auth/camera-storage', { mac, blob, hmac })) as {
		data?: Partial<CameraStorageKeyEntry>;
	};
	return {
		blob,
		hmac,
		createdAt: res?.data?.createdAt ?? new Date().toISOString(),
	};
};

/**
 * Fetches a device's encrypted recording-key backup from the cloud.
 *
 * @remarks
 * The endpoint returns a map of MAC to its backup history (newest first). This
 * helper returns only the newest entry; callers that must handle key rotation
 * (a recording may use any historical key) should use {@link getCameraStorageKeys}.
 *
 * @param mac - The normalised device MAC (12 lowercase hex digits).
 * @returns The newest encrypted backup entry, or `null` when none is stored.
 */
export const getCameraStorageKey = async (mac: string): Promise<CameraStorageKeyEntry | null> => {
	if (!mac) return null;
	const entries = await getCameraStorageKeys(mac);
	return entries[0] ?? null;
};

/**
 * Fetches a device's full encrypted recording-key backup **history** from the
 * cloud, newest first.
 *
 * @remarks
 * Key **rotation** appends a new backup per provisioning, and a recording does
 * not announce which key it was encrypted with, so callers may need to try every
 * historical backup. The endpoint returns a map of MAC to its backup history.
 *
 * @param mac - The normalised device MAC (12 lowercase hex digits).
 * @returns The device's backup history (newest first), or an empty array.
 */
export const getCameraStorageKeys = async (mac: string): Promise<CameraStorageKeyEntry[]> => {
	if (!mac) return [];
	const result = (await cameraCloudApi.get(
		`/auth/camera-storage?mac=${encodeURIComponent(mac)}`,
	)) as CameraStorageKeyResponse;

	const entries = result?.[mac];
	return Array.isArray(entries) ? entries : [];
};

/**
 * Fetches the encrypted recording-key backups for every camera on the account.
 *
 * @remarks
 * Returns a map of device MAC (12 lowercase hex digits) to that camera's backup
 * history (an array of {@link CameraStorageKeyEntry}, newest first).
 *
 * @returns The full camera-storage map, or an empty object when none are stored.
 */
export const getAllCameraStorageKeys = async (): Promise<CameraStorageKeyResponse> => {
	const result = (await cameraCloudApi.get('/auth/camera-storage')) as CameraStorageKeyResponse;
	return result ?? {};
};

/**
 * Bulk-fetches the latest thumbnail for every camera device.
 *
 * @returns A map (optionally wrapped in `data`) of device id to its thumbnail
 * payload (`{ encrypted, imageUrls }`).
 */
export const getAllLatestImages = async (): Promise<unknown> => {
	return cameraCloudApi.get('/v1/recordings/thumbnails');
};

/**
 * Fetches the latest thumbnail for a device.
 *
 * @param deviceId - The device id.
 * @returns The thumbnail response, including the `encrypted` flag and `imageUrls`.
 */
export const getDeviceLatestImage = async (deviceId: string): Promise<DeviceThumbnailResponse> => {
	return (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}/thumbnail`,
	)) as DeviceThumbnailResponse;
};
